import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";
import SpeedIcon from "@mui/icons-material/Speed";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

import MetabaseList from "./MetabaseList";
import type { AeronetViewProps } from "../types";
import metabasePlaceholderImg from "./metabase_dashboard_placeholder.jpg";

export const MetabaseReportingPlaceholder: React.FC<Partial<AeronetViewProps>> = () => {
  const [showPreview, setShowPreview] = useState(true);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, margin: "0 auto" }}>
      {/* Top Header & Context Card */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          bgcolor: "#f8fafc",
          border: "1px solid #e2e8f0",
        }}
      >
        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
          <Grid item xs={12} md={8}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
              <AssessmentOutlinedIcon sx={{ color: "#018a90", fontSize: 30 }} />
              <Typography variant="h5" fontWeight={700} color="#1e293b">
                Metabase Reporting & Analytics
              </Typography>
              <Chip
                label="BI Workspace"
                size="small"
                sx={{
                  bgcolor: "rgba(1, 138, 144, 0.1)",
                  color: "#018a90",
                  fontWeight: 600,
                  fontSize: "12px",
                }}
              />
            </Stack>
            <Typography variant="body2" color="#64748b" sx={{ maxWidth: 750 }}>
              Interactive business intelligence, fleet utilization telemetry, maintenance downtime analysis, and custom SQL reporting dashboards powered by Metabase.
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} sx={{ display: "flex", gap: 1, justifyContent: { xs: "flex-start", md: "flex-end" } }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setShowPreview((prev) => !prev)}
              endIcon={showPreview ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{
                textTransform: "none",
                borderColor: "#cbd5e1",
                color: "#475569",
                borderRadius: 1,
                "&:hover": { borderColor: "#018a90", color: "#018a90" },
              }}
            >
              {showPreview ? "Hide Preview" : "Show Preview"}
            </Button>
            <Button
              variant="outlined"
              size="small"
              endIcon={<OpenInNewIcon fontSize="small" />}
              href="https://www.metabase.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                textTransform: "none",
                borderColor: "#cbd5e1",
                color: "#475569",
                borderRadius: 1,
                "&:hover": {
                  borderColor: "#018a90",
                  color: "#018a90",
                },
              }}
            >
              Docs
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Collapsible Dashboard Preview */}
      <Collapse in={showPreview}>
        <Stack spacing={3} sx={{ mb: 4 }}>
          {/* The four tiles and the dashboard image below are a fixed sample —
             no Metabase question backs any of them. Labelled here rather than
             per-tile so the caption cannot be read in isolation from the
             number it qualifies. */}
          <Alert
            severity="warning"
            icon={<WarningAmberOutlinedIcon />}
            sx={{
              borderRadius: 2,
              bgcolor: "#fffbeb",
              border: "1px solid #fde68a",
              color: "#92400e",
              "& .MuiAlert-icon": { color: "#b45309" },
            }}
          >
            <strong>Illustrative mockup — not live data.</strong> The figures and dashboard image in this preview are a
            fixed sample of what Metabase reporting will look like. They are not measured from this tenant. The report
            list underneath is live.
          </Alert>

          {/* Quick Metrics Bar */}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 2, bgcolor: "#ffffff" }}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        FLEET UTILIZATION
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="#0f172a">
                        88.4%
                      </Typography>
                    </Box>
                    <SpeedIcon sx={{ color: "#018a90", opacity: 0.8 }} />
                  </Stack>
                  <Typography variant="caption" color="#16a34a" fontWeight={600} sx={{ display: "block", mt: 0.5 }}>
                    Active fleet target met
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 2, bgcolor: "#ffffff" }}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        FLIGHT HOURS MTD
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="#0f172a">
                        1,420 hrs
                      </Typography>
                    </Box>
                    <FlightTakeoffIcon sx={{ color: "#0284c7", opacity: 0.8 }} />
                  </Stack>
                  <Typography variant="caption" color="#0284c7" fontWeight={600} sx={{ display: "block", mt: 0.5 }}>
                    +5% vs previous month
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 2, bgcolor: "#ffffff" }}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        MAINTENANCE WORK ORDERS
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="#0f172a">
                        12 Open
                      </Typography>
                    </Box>
                    <BuildCircleOutlinedIcon sx={{ color: "#f59e0b", opacity: 0.8 }} />
                  </Stack>
                  <Typography variant="caption" color="#f59e0b" fontWeight={600} sx={{ display: "block", mt: 0.5 }}>
                    Scheduled inspection queue
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 2, bgcolor: "#ffffff" }}>
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight={500}>
                        INVENTORY TURN RATE
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="#0f172a">
                        4.2x
                      </Typography>
                    </Box>
                    <QueryStatsIcon sx={{ color: "#8b5cf6", opacity: 0.8 }} />
                  </Stack>
                  <Typography variant="caption" color="text.secondary" fontWeight={500} sx={{ display: "block", mt: 0.5 }}>
                    Stores & procurement cycle
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Embedded Placeholder Graphic */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              border: "1px solid #e2e8f0",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.06)",
              bgcolor: "#ffffff",
            }}
          >
            <Box
              sx={{
                px: 3,
                py: 2,
                bgcolor: "#0f172a",
                color: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: "#10b981",
                  }}
                />
                <Typography variant="subtitle2" fontWeight={600} sx={{ color: "#f8fafc" }}>
                  AeroFleet Executive BI Dashboard Preview
                </Typography>
              </Stack>
              <Chip
                size="small"
                label="Sample View"
                sx={{
                  bgcolor: "rgba(255, 255, 255, 0.15)",
                  color: "#ffffff",
                  fontSize: "11px",
                  height: 22,
                }}
              />
            </Box>

            <Box sx={{ position: "relative", width: "100%", bgcolor: "#f8fafc" }}>
              <img
                src={metabasePlaceholderImg}
                alt="Metabase Aviation Reporting Dashboard Preview"
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                  objectFit: "contain",
                }}
              />
            </Box>
          </Paper>
        </Stack>
      </Collapse>

      {/* Reports Directory (DataGrid) */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: "1px solid #e2e8f0",
          overflow: "hidden",
        }}
      >
        <MetabaseList />
      </Paper>
    </Box>
  );
};

export default MetabaseReportingPlaceholder;
